import { Field, Formik, Form as FormikForm } from 'formik';
import React from 'react';
import { Col, Form, InputGroup, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../store/store';
import OffersStepProps from '../../../../models/offer-step.interface';
import OfferStepActions from '../../../../components/ui/offer-step-actions/OfferStepActions';
import { date, object, string } from 'yup';
import {
  setStepOneCount,
  setVacancyFormMainInfo,
} from '../../../../store/slices/vacancyCreateSlice';
import { useTranslation } from 'react-i18next';
import { ListItem } from '../../../../models/list-item';
import Map from "../../../../components/autocomplete/AddressAutocomplete";
import RestrictedInput from "../../../../components/ui/restricted-input/RestrictedInput";

const OfferStep1 = ({
  currentStep,
  goToPreviousStep,
  goToNextStep,
}: OffersStepProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const vacancyForm = useSelector(
    (state: RootState) => state.vacancyCreateReducer.vacancy
  );
  const sectorOptions: ListItem[] = t('sectorOptions', { returnObjects: true });
  const validationSchema = object({
    sector: string().required(t('errors.fieldRequired')),
    position: string().required(t('errors.fieldRequired')),
    places: string().required(t('errors.fieldRequired')),
    address: string().required(t('errors.fieldRequired')),
    city: string().required(t('errors.fieldRequired')),
    country: string().required(t('errors.fieldRequired')),
    addressStair: string().required(t('errors.fieldRequired')),
    date: date().required(t('errors.fieldRequired')),
  });

  function onFormChange(values: any) {
    let count = 0;
    for (const [key, value] of Object.entries(values)) {
      const val = (value as unknown as string) ? count++ : count;
    }

    // This is a hack due to the address elements being individual fields
    if (count > 5) {
      count = 5;
    }

    dispatch(setStepOneCount(count));
  }

  return (
    <>
      <h2 className='page-heading-2 text-dark-blue-alt my-3 fw-bold'>
        {t('createOffer.step1.title')}
      </h2>
      <div className='mt-5'>
        <Formik
          validate={(values) => {
            onFormChange(values);
          }}
          initialValues={{
            sector: vacancyForm.sector,
            position: vacancyForm.position,
            places: vacancyForm.number_of_vacancies,
            address: vacancyForm.address,
            addressNumber: vacancyForm.addressNumber,
            addressStair: vacancyForm.addressStair,
            addressFloor: vacancyForm.addressFloor,
            city: vacancyForm.city,
            country: vacancyForm.country,
            latitude: vacancyForm.latitude,
            longitude: vacancyForm.longitude,
            date: vacancyForm.end_date,
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            dispatch(setVacancyFormMainInfo(values));
            goToNextStep();
          }}
        >
          {({ values, errors, touched, setFieldValue }) => (
            <FormikForm>
              <Form.Group
                className='mb-3'
                controlId='exampleForm.ControlInput1'
              >
                <Form.Label className='offer-form-label mb-0'>
                  {t('createOffer.card1.key1')}
                </Form.Label>
                <InputGroup hasValidation>
                  <Field
                    as='select'
                    name='sector'
                    className='form-control modal-input'
                  >
                    <option value=''></option>
                    {sectorOptions.map((item, index) => (
                      <option key={index} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </Field>
                  {errors.sector && touched.sector ? (
                    <p className='text-danger position-absolute error-message candidate'>
                      {errors.sector}
                    </p>
                  ) : null}
                </InputGroup>
              </Form.Group>
              <Row>
                <Col md={6}>
                  <Form.Group
                    className='mb-3'
                    controlId='exampleForm.ControlInput1'
                  >
                    <Form.Label className='offer-form-label mb-0'>
                      {t('companyVacancyDetails.position')}
                    </Form.Label>
                    <InputGroup hasValidation>
                      <Field
                        type='text'
                        name='position'
                        placeholder=''
                        className='form-control modal-input'
                      />
                      {errors.position && touched.position ? (
                        <p className='text-danger position-absolute error-message candidate'>
                          {errors.position}
                        </p>
                      ) : null}
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group
                    className='mb-3'
                    controlId='exampleForm.ControlInput1'
                  >
                    <Form.Label className='offer-form-label mb-0'>
                      {t('createOffer.step1.vacanciesAmount')}
                    </Form.Label>
                    <InputGroup hasValidation>
                      <Field
                        type='number'
                        min={0}
                        name='places'
                        className='form-control modal-input'
                        placeholder=''
                      />
                      {errors.places && touched.places ? (
                        <p className='text-danger position-absolute error-message candidate'>
                          {errors.places}
                        </p>
                      ) : null}
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

                <Row className='form-fields candidate'>
                    <Form.Label className='offer-form-label mb-0'>
                        {t('createOffer.card1.key2')}
                    </Form.Label>
                    <Row>
                        <Map
                            initialText={values.address ?? ""}
                            country="es"
                            minLength={3}
                            debounceMs={300}
                            onSelect={(a) => {
                                // push selected values into Formik state
                                setFieldValue("address", a.street ?? "");
                                setFieldValue("addressNumber", a.houseNumber ?? "");
                                setFieldValue("city", a.city ?? "");
                                setFieldValue("country", a.country ?? "");
                                setFieldValue("addressStair", a.postalCode ?? "");
                                setFieldValue("latitude", a.lat ?? null);
                                setFieldValue("longitude", a.lng ?? null);
                            }}
                        />
                    </Row>
                    <Row>
                        <Form.Group
                            as={Col}
                            md={8}
                            xs={12}
                            controlId='validationCustom01'
                        >
                            <Form.Label className='offer-form-label'>
                                {t('companyRegistration.step2.addressLabel')}
                            </Form.Label>
                            <InputGroup hasValidation>
                                <Field
                                    className='form-control mt-0 px-4 py-3'
                                    type='text'
                                    placeholder=''
                                    aria-describedby='inputGroupPrepend'
                                    name='address'
                                    id='address1Field'
                                />
                                {errors.address && touched.address ? (
                                    <p className='text-danger position-absolute error-message candidate'>
                                        {errors.address}
                                    </p>
                                ) : null}
                            </InputGroup>
                        </Form.Group>
                        <Form.Group
                            as={Col}
                            md={4}
                            xs={12}
                            controlId='validationCustom02'
                            className='mobile-mt-2'
                        >
                            <Form.Label className='offer-form-label'>
                                {t('companyRegistration.step2.addressNumberLabel')}
                            </Form.Label>
                            <InputGroup hasValidation>
                                <Field>
                                    {() => (
                                        <RestrictedInput
                                            setFieldValue={setFieldValue}
                                            fieldValue={values.addressNumber}
                                            valueName='addressNumber'
                                            restriction='number'
                                            id="streetnumber"
                                            className='form-control mt-0 px-4 py-3'
                                        />
                                    )}
                                </Field>
                                {errors.addressNumber && touched.addressNumber ? (
                                    <p className='text-danger position-absolute error-message candidate'>
                                        {errors.addressNumber}
                                    </p>
                                ) : null}
                            </InputGroup>
                        </Form.Group>
                    </Row>
                    <Row>
                        <Form.Group
                            as={Col}
                            md={4}
                            xs={12}
                            controlId='validationCustom03'
                            className='mobile-mt-2'
                        >
                            <Form.Label className='offer-form-label mt-2'>
                                {t('companyRegistration.step2.addressFloorLabel')}
                            </Form.Label>
                            <InputGroup hasValidation>
                                <Field
                                    className='form-control mt-0 px-4 py-3'
                                    type='text'
                                    placeholder=''
                                    aria-describedby='inputGroupPrepend'
                                    name='addressFloor'
                                    id='addressFloor'
                                />
                            </InputGroup>
                        </Form.Group>
                        <Form.Group
                            as={Col}
                            md={4}
                            xs={12}
                            controlId='validationCustom03'
                            className='mobile-mt-2'
                        >
                            <Form.Label className='offer-form-label mt-2'>
                                {t('companyRegistration.step2.addressStairLabel')}
                            </Form.Label>
                            <InputGroup hasValidation>
                                <Field
                                    className='form-control mt-0 px-4 py-3'
                                    type='text'
                                    placeholder=''
                                    aria-describedby='inputGroupPrepend'
                                    name='addressStair'
                                    id='addressStair'
                                />
                                {errors.addressStair && touched.addressStair ? (
                                    <p className='text-danger position-absolute error-message candidate'>
                                        {errors.addressStair}
                                    </p>
                                ) : null}
                            </InputGroup>
                        </Form.Group>

                        <Form.Group
                            as={Col}
                            md={4}
                            xs={12}
                            controlId='validationCustom01'
                            className='mobile-mt-2'
                        >
                            <Form.Label className='offer-form-label mt-2'>
                                {t('companyRegistration.step2.cityLabel')}
                            </Form.Label>
                            <InputGroup hasValidation>
                                <Field>
                                    {() => (
                                        <RestrictedInput
                                            setFieldValue={setFieldValue}
                                            fieldValue={values.city}
                                            valueName='city'
                                            restriction='text'
                                            id="city"
                                            className='form-control mt-0 px-4 py-3'
                                        />
                                    )}
                                </Field>
                                {errors.city && touched.city ? (
                                    <p className='text-danger position-absolute error-message candidate'>
                                        {errors.city}
                                    </p>
                                ) : null}
                            </InputGroup>
                        </Form.Group>
                    </Row>

                    <Row>
                        <Form.Group as={Col} md={4} xs={12} className='mobile-mt-2'>
                            <Form.Label className='offer-form-label mt-2'>
                                {t('candidateRegistration.step1.regionLabel')}
                            </Form.Label>
                            <InputGroup hasValidation>
                                <Field>
                                    {() => (
                                        <RestrictedInput
                                            setFieldValue={setFieldValue}
                                            fieldValue={values.country}
                                            valueName='country'
                                            restriction='text'
                                            id="country"
                                            className='form-control mt-0 px-4 py-3'
                                        />
                                    )}
                                </Field>
                                {errors.country && touched.country ? (
                                    <p className='text-danger position-absolute error-message candidate'>
                                        {errors.country}
                                    </p>
                                ) : null}
                            </InputGroup>
                        </Form.Group>
                    </Row>
                </Row>

              <Row>
                <Col md={6}>
                  <Form.Group
                    className='mb-3 mt-3'
                    controlId='exampleForm.ControlInput1'
                  >
                    <Form.Label className='offer-form-label mb-0'>
                      {t('createOffer.step1.endDate')}
                    </Form.Label>
                    <InputGroup hasValidation>
                      <Field
                        type='date'
                        name='date'
                        min={new Date().toISOString().split('T')[0]}
                        className='form-control modal-input'
                      />
                      {errors.date && touched.date ? (
                        <p className='text-danger position-absolute error-message candidate'>
                          {errors.date}
                        </p>
                      ) : null}
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              <OfferStepActions
                currentStep={currentStep}
                goToPreviousStep={goToPreviousStep}
                goToNextStep={goToNextStep}
              />
            </FormikForm>
          )}
        </Formik>
      </div>
    </>
  );
};

export default OfferStep1;